import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@schaeffler-commerce/design-system";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  ProductComparisonContent,
  type ProductComparisonContentRef,
} from "~/features/compareProducts/components/ProductComparisonContent";
import type { PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useDispatch } from "react-redux";
import { transformPdpProduct } from "~/features/productDetails/transformers/PdpPageProductDataTransformer";
import { addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";
import { useProductComparisonQuery, useUpdateShowDifferenceMutation } from "../api/productComparisonQuery";
import { Overlay } from "~/common/components/Overlay/Overlay";
import { ProductComparisonSkeleton } from "./ProductComparisonSkeleton";
import { useTranslation } from "react-i18next";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import { pinProduct, unPinProduct } from "../api/comparePinProduct";
import { isSessionResolved } from "~/domains/user/sessionRuntime";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { ComparisonOverlayHeader } from "./ComparisonOverlayStyles";

const ComparisionDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const pageRef = useRef<ProductComparisonContentRef>(null);
  const screenSize = useNumericScreenSize();
  const isTablet = screenSize <= useNumericScreenSize.tablet;
  const [showSidebar, setShowSidebar] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [clearError, setClearError] = useState(false);
  const dispatch = useDispatch();
  const [products, setProducts] = useState<PdpPageProductData[]>([]);
  const [pinnedProductCodes, setPinnedProductCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: comparisonData, isPending } = useProductComparisonQuery();
  const productCodes = comparisonData?.productCodes ?? [];
  const updateOnlyDifference = useUpdateShowDifferenceMutation();
  const showOnlyDifference = comparisonData?.showDifference ?? false;

  useEffect(() => {
    if (isPending && isSessionResolved() && userIsLoggedIn()) {
      return;
    }
    if (!comparisonData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- SYNC-EXTERNAL: mirrors the RTK Query comparison snapshot (plus a Redux dispatch) into local state; pinnedProductCodes is also optimistically handler-written, so it cannot be render-derived
      setLoading(false);
      return;
    }
    const transformedProducts = comparisonData.products.map((item) => transformPdpProduct(item, null));
    setProducts(transformedProducts);
    dispatch(addBaseProductDataAction(transformedProducts));
    setPinnedProductCodes(comparisonData.products.filter((product) => product.pinned).map((product) => product.code));
    setLoading(false);
  }, [comparisonData, dispatch, isPending]);

  const toggleOnlyDifference = useCallback(async () => {
    try {
      const nextValue = !showOnlyDifference;
      await updateOnlyDifference.mutateAsync(nextValue);
    } catch (error) {
      console.error("Failed to update showOnlyDifference preference", error);
    }
  }, [showOnlyDifference, updateOnlyDifference]);

  const hideDrawer = () => {
    setShowSidebar(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await pageRef.current?.clearAll();
      setClearError(false);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Failed to clear product comparison", error);
      setClearError(true);
    }
  };
  const handleCancelDelete = () => {
    setClearError(false);
    setShowDeleteConfirmation(false);
  };
  const onPinClick = async (productCode: string) => {
    try {
      await pinProduct(productCode);
      setPinnedProductCodes((current) => (current.includes(productCode) ? current : [...current, productCode]));
    } catch (error) {
      console.error("Failed to pin product:", error);
    }
  };

  const onUnPinClick = async (productCode: string) => {
    try {
      await unPinProduct(productCode);
      setPinnedProductCodes((current) => current.filter((code) => code !== productCode));
    } catch (error) {
      console.error("Failed to unPin product:", error);
    }
  };

  return (
    <>
      <Overlay open={true}>
        <ComparisonOverlayHeader>
          <Stack direction={"row"} sx={{
            justifyContent: "space-between"
          }}>
            <Typography variant="headlineSmall" component="b">
              {t("comparision.overlay.title")}
            </Typography>
            <Stack direction={"row"} sx={{
              gap: 1
            }}>
              {productCodes.length > 0 && isTablet && (
                <IconButton onClick={() => setShowSidebar(true)} disabled={loading}>
                  <MoreVertIcon />
                </IconButton>
              )}
              {productCodes.length > 0 && (
                <TooltipWithArrow title={t("comparision.overlay.card.deleteTootlip")} placement="top">
                  <IconButton onClick={() => setShowDeleteConfirmation(true)} disabled={loading}>
                    <DeleteOutlineOutlinedIcon />
                  </IconButton>
                </TooltipWithArrow>
              )}
              <IconButton onClick={onClose}>
                <CloseOutlinedIcon />
              </IconButton>
            </Stack>
          </Stack>
        </ComparisonOverlayHeader>

        {loading ? (
          <ProductComparisonSkeleton />
        ) : (
          <ProductComparisonContent
            ref={pageRef}
            data={products}
            maxSize={comparisonData?.maxSize ?? 0}
            showSidebar={showSidebar}
            hideDrawer={hideDrawer}
            showOnlyDifference={showOnlyDifference}
            toggleOnlyDifference={toggleOnlyDifference}
            pinnedProductCodes={pinnedProductCodes}
            onPinClick={onPinClick}
            onUnPinClick={onUnPinClick}
          />
        )}
      </Overlay>
      <Dialog
        open={showDeleteConfirmation}
        maxWidth="tablet"
        slotProps={{
          paper: {
            sx: {
              width: "312px",
              maxWidth: "312px",
            },
          }
        }}
      >
        <DialogHeader onClose={handleCancelDelete}>
          <DialogTitle>{t("comparision.overlay.deletePopup.title")}</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <Typography>{t("comparision.overlay.deletePopup.content")}</Typography>
          {clearError && <Typography color="error">{t("comparisonSnack.failedToRemove")}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleConfirmDelete}>
            {t("comparision.overlay.deletePopup.yes")}
          </Button>
          <Button variant="text" onClick={handleCancelDelete}>
            {t("comparision.overlay.deletePopup.no")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ComparisionDialog;
